import { useAuth } from '@/lib'
import { CampaignStatusBadge } from '@/ui'
import { CogMenu, ConfirmationDialog, ConfirmationDialogRef, Glyphs } from '@/ui'
import { DashboardCampaignModel, DashboardViewModel } from '@/ui'
import { imgixUrl } from '@/ui/utilities/imgix'
import ctl from '@netlify/classnames-template-literals'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { campaignMenuActions, hasAnyCampaignMenuAction } from './campaign-menu-actions'
import { BudgetWithBreakdown, formatCampaignDateRange } from './dashboard-helpers'

type CampaignTableProps = {
	campaigns: DashboardCampaignModel[]
	linkRenderer: DashboardViewModel['renderers']['campaignLink']
	onArchive: DashboardViewModel['events']['onArchiveCampaign']
	onUnarchive: DashboardViewModel['events']['onUnarchiveCampaign']
	onCopy: DashboardViewModel['events']['onCopyCampaign']
	archivingCampaignIds: string[]
	unarchivingCampaignIds: string[]
	copyingCampaignIds: string[]
}

export function CampaignTable({
	campaigns,
	linkRenderer,
	onArchive,
	onUnarchive,
	onCopy,
	archivingCampaignIds,
	unarchivingCampaignIds,
	copyingCampaignIds,
}: CampaignTableProps) {
	const { t } = useTranslation()

	return (
		<div className="flex flex-col items-center w-full pb-20">
			<div className="w-full overflow-x-auto" style={{ maxWidth: '1440px' }}>
				<div className="bg-white rounded-xl shadow-sm min-w-[1000px]">
					<table className="w-full table-fixed">
						<thead>
							<tr className="border-b border-grey-200">
								<th className="text-left text-sm font-medium text-grey-600 px-6 py-4 w-[28%]">
									{t('dashboard.table.campaign')}
								</th>
								<th className="text-left text-sm font-medium text-grey-600 px-6 py-4 w-[12%]">
									{t('dashboard.table.location')}
								</th>
								<th className="text-left text-sm font-medium text-grey-600 px-6 py-4 w-[12%]">
									{t('dashboard.table.status')}
								</th>
								<th className="text-left text-sm font-medium text-grey-600 px-6 py-4 w-[18%]">
									{t('dashboard.table.dates')}
								</th>
								<th className="text-left text-sm font-medium text-grey-600 px-6 py-4 w-[12%]">
									{t('dashboard.table.budget')}
								</th>
								<th className="text-right text-sm font-medium text-grey-600 px-6 py-4 w-[18%]">
									{t('dashboard.table.actions')}
								</th>
							</tr>
						</thead>
						<tbody>
							{campaigns.map((campaign) => (
								<CampaignTableRow
									key={campaign.id}
									campaign={campaign}
									linkRenderer={linkRenderer}
									onArchive={onArchive}
									onUnarchive={onUnarchive}
									onCopy={onCopy}
									isArchiving={archivingCampaignIds.includes(campaign.id)}
									isUnarchiving={unarchivingCampaignIds.includes(campaign.id)}
									isCopying={copyingCampaignIds.includes(campaign.id)}
								/>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

type CampaignTableRowProps = {
	campaign: DashboardCampaignModel
	linkRenderer: DashboardViewModel['renderers']['campaignLink']
	onArchive: DashboardViewModel['events']['onArchiveCampaign']
	onUnarchive: DashboardViewModel['events']['onUnarchiveCampaign']
	onCopy: DashboardViewModel['events']['onCopyCampaign']
	isArchiving: boolean
	isUnarchiving: boolean
	isCopying: boolean
}

function CampaignTableThumbnail({ campaign }: { campaign: DashboardCampaignModel }) {
	const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null)
	const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null)

	const imageUrl = campaign.status === 'draft'
		? imgixUrl(campaign.image, { w: 48, h: 48, fit: 'crop', dpr: 2 })
		: campaign.image

	if (imageUrl && imageUrl !== failedImageUrl) {
		return (
			<img
				className="w-12 h-12 rounded-lg bg-grey-200 object-cover flex-shrink-0"
				src={imageUrl}
				alt=""
				onError={() => setFailedImageUrl(imageUrl)}
			/>
		)
	}

	const logoUrl = campaign.branding?.logoUrl
		? imgixUrl(campaign.branding.logoUrl, { w: 48, h: 48, fit: 'max', dpr: 2 })
		: null

	if (campaign.branding && logoUrl && logoUrl !== failedLogoUrl) {
		return (
			<div
				className="w-12 h-12 rounded-lg flex-shrink-0 flex items-center justify-center p-1.5"
				style={{ backgroundColor: campaign.branding.background }}
			>
				<img
					className="max-w-full max-h-full object-contain"
					src={logoUrl}
					alt=""
					onError={() => setFailedLogoUrl(logoUrl)}
				/>
			</div>
		)
	}

	return <div className="w-12 h-12 rounded-lg bg-grey-200 flex-shrink-0" />
}

function CampaignTableRow({
	campaign,
	linkRenderer,
	onArchive,
	onUnarchive,
	onCopy,
	isArchiving,
	isUnarchiving,
	isCopying,
}: CampaignTableRowProps) {
	const { t } = useTranslation()
	const { can } = useAuth()
	const archiveConfirmationRef = useRef<ConfirmationDialogRef>(null)
	const unarchiveConfirmationRef = useRef<ConfirmationDialogRef>(null)

	const handleArchiveClick = async () => {
		try {
			await archiveConfirmationRef.current?.confirm({
				title: t('dashboard.card.archiveTitle'),
				message: t('dashboard.card.archiveMessage', {
					name: campaign.title || t('dashboard.card.unnamedCampaign'),
				}),
				confirmText: t('dashboard.card.confirmArchive'),
				cancelText: t('dashboard.card.cancel'),
			})
			await onArchive(campaign.id, campaign.title)
		} catch {
			// cancelled
		}
	}

	const handleUnarchiveClick = async () => {
		try {
			await unarchiveConfirmationRef.current?.confirm({
				title: t('dashboard.card.unarchiveTitle'),
				message: t('dashboard.card.unarchiveMessage', {
					name: campaign.title || t('dashboard.card.unnamedCampaign'),
				}),
				confirmText: t('dashboard.card.confirmUnarchive'),
				cancelText: t('dashboard.card.cancel'),
			})
			await onUnarchive(campaign.id, campaign.title)
		} catch {
			// cancelled
		}
	}

	const handleCopyClick = async () => {
		await onCopy(campaign.id, campaign.title)
	}

	const menuActions = campaignMenuActions(campaign, can)

	const isProcessing = isArchiving || isUnarchiving || isCopying

	return (
		<>
			<tr
				className={ctl(`
					border-b border-grey-100 last:border-b-0 hover:bg-grey-50 transition-colors
					${isProcessing ? 'opacity-40 pointer-events-none' : ''}
				`)}
			>
				<td className="px-6 py-4">
					{linkRenderer(campaign.id, campaign.status, campaign.isArchived, {
						className: 'flex items-center gap-3',
						children: (
							<>
								<CampaignTableThumbnail campaign={campaign} />
								<span className="font-medium text-grey-900 truncate max-w-xs">
									{campaign.title || 'Untitled Campaign'}
								</span>
							</>
						),
					})}
				</td>
				<td className="px-6 py-4">
					<div className="flex items-center gap-1 text-sm text-grey-600">
						<div className="size-2.5 text-grey-400">
							<Glyphs.micro.PinIcon />
						</div>
						<span className="truncate max-w-32">{campaign.location}</span>
					</div>
				</td>
				<td className="px-6 py-4">
					<div className="w-fit">
						<CampaignStatusBadge
							status={campaign.status}
							startedAt={campaign.startedAt}
							endsAt={campaign.endsAt}
							isEvergreen={campaign.isEvergreen}
						/>
					</div>
				</td>
				<td className="px-6 py-4">
					<span className="text-sm text-grey-600">
						{campaign.isEvergreen
							? t('dashboard.ongoing')
							: formatCampaignDateRange(campaign.startedAt, campaign.endsAt)}
					</span>
				</td>
				<td className="px-6 py-4">
					{!campaign.isEvergreen && (
						<BudgetWithBreakdown
							className="text-sm text-grey-600"
							budget={campaign.budget}
							budgetBreakdown={campaign.budgetBreakdown}
						/>
					)}
				</td>
				<td className="px-6 py-4">
					<div className="flex items-center justify-end gap-2 whitespace-nowrap">
						{hasAnyCampaignMenuAction(menuActions) && (
							<CogMenu
								className="text-grey-600"
								actions={[
									...(menuActions.clone
										? [{
											id: 'clone',
											label: t('dashboard.card.action.clone'),
											icon: <Glyphs.core.CopyIcon />,
											onClick: handleCopyClick,
										}]
										: []),
									...(menuActions.archive
										? [{
											id: 'archive',
											label: t('dashboard.card.action.archive'),
											icon: <Glyphs.core.ArchiveIcon />,
											onClick: handleArchiveClick,
										}]
										: []),
									...(menuActions.unarchive
										? [{
											id: 'unarchive',
											label: t('dashboard.card.action.unarchive'),
											icon: <Glyphs.core.ArchiveIcon />,
											onClick: handleUnarchiveClick,
										}]
										: []),
								]}
							/>
						)}
						{linkRenderer(campaign.id, campaign.status, campaign.isArchived, {
							className: '',
							children: (
								<span
									className={ctl(`
										flex items-center gap-0.5 text-sm tracking-wide
										text-brand-900 bg-purple-100 pl-3.5 pr-2 py-1.5 rounded-full
									`)}
								>
									<span className="relative">
										{campaign.status === 'draft'
											? t('dashboard.table.edit')
											: t('dashboard.table.overview')}
									</span>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
										strokeWidth={1.5}
										stroke="currentColor"
										className="size-4"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="m8.25 4.5 7.5 7.5-7.5 7.5"
										/>
									</svg>
								</span>
							),
						})}
					</div>
				</td>
			</tr>
			<ConfirmationDialog ref={archiveConfirmationRef} />
			<ConfirmationDialog ref={unarchiveConfirmationRef} />
		</>
	)
}
